/**
 * The portfolio tables, pivoted the way the page shows them.
 *
 * The backend sends long rows — one per house, year and line item — which is
 * what the Analyst queries. A person reads them as a grid: a plat per row on
 * the portfolio view, a house per row inside a plat. Every number in a grid
 * cell is one of those rows, looked up by house and line item, never a sum the
 * page makes up — except the two the page labels as worked out: vacancy loss
 * as a share of rent billed, and the portfolio total row.
 */

import type {
	AddedColumn,
	AddedColumnValue,
	Basis,
	Portfolio,
	PortfolioFact,
	PortfolioHouse,
	PortfolioPlatFact,
} from "../../services/portfolioDataService";

export type Origin = "workbook" | "worked" | "split";

/** What the page calls each basis, in the loan tape's three-word style. */
export const ORIGIN_OF_BASIS: Record<Basis, Origin> = {
	actual: "workbook",
	derived: "workbook",
	allocated: "split",
};

export const ORIGIN_LABEL: Record<Origin, string> = {
	workbook: "From your workbook",
	worked: "Worked out",
	split: "Split across the plat",
};

export const ORIGIN_DOT: Record<Origin, string> = {
	workbook: "bg-[#008782]",
	worked: "bg-[#444ce7]",
	split: "bg-[#dc6803]",
};

export const ORIGIN_PILL: Record<Origin, string> = {
	workbook: "bg-[#E6F3F3] text-[#004C4B]",
	worked: "bg-[#eef4ff] text-[#3538cd]",
	split: "bg-[#fffaeb] text-[#b54708]",
};

export const ORIGIN_TEXT: Record<Origin, string> = {
	workbook: "text-[#004C4B]",
	worked: "text-[#3538cd]",
	split: "text-[#b54708]",
};

/** A vacancy loss this share of rent billed or more is flagged on the row. */
export const SHORTFALL_FLAG = 0.1;

export interface HouseColumn {
	/** The line item the cell reads, or a key the page works out. */
	key: string;
	label: string;
	/** Said under the header: the unit and period, e.g. "$ / yr". */
	unit: string;
	kind: "money" | "ratio" | "pct" | "text";
	/** Shown in the grid; the rest are in the cell panel and the Analyst's tables. */
	primary: boolean;
}

/**
 * The house grid's columns. The figures a lender reads first lead — net
 * income, what is left after debt and capital costs, and debt coverage — then
 * rent and collections.
 */
export const HOUSE_COLUMNS: readonly HouseColumn[] = [
	{ key: "net_income", label: "Net income", unit: "$ / yr", kind: "money", primary: true },
	{ key: "net_after_principal_and_capex", label: "Net after debt & capex", unit: "$ / yr", kind: "money", primary: true },
	{ key: "dcr", label: "DCR", unit: "ratio", kind: "ratio", primary: true },
	{ key: "rent_billed", label: "Rent billed", unit: "$ / yr", kind: "money", primary: true },
	{ key: "rent_collected", label: "Rent collected", unit: "$ / yr", kind: "money", primary: true },
	{ key: "vacancy_pct", label: "Vacancy loss", unit: "% of billed", kind: "pct", primary: true },
	{ key: "current_monthly_rent", label: "Rent", unit: "$ / mo", kind: "money", primary: true },
	{ key: "other_fee_income", label: "Other income", unit: "$ / yr", kind: "money", primary: false },
	{ key: "landlord_nonreimbursable_expenses", label: "Non-reimbursable repairs", unit: "$ / yr", kind: "money", primary: false },
	{ key: "property_taxes", label: "Property taxes", unit: "$ / yr", kind: "money", primary: false },
	{ key: "insurance", label: "Insurance", unit: "$ / yr", kind: "money", primary: false },
	{ key: "management_fee", label: "Management fee", unit: "$ / yr", kind: "money", primary: false },
	{ key: "hoa", label: "HOA", unit: "$ / yr", kind: "money", primary: false },
	{ key: "interest_expense", label: "Interest", unit: "$ / yr", kind: "money", primary: false },
	{ key: "replacement_costs", label: "Replacement costs", unit: "$ / yr", kind: "money", primary: false },
	{ key: "principal_paid", label: "Principal", unit: "$ / yr", kind: "money", primary: false },
	{ key: "loan_balance", label: "Loan balance", unit: "$", kind: "money", primary: false },
	{ key: "tax_assessed_value", label: "Assessed value", unit: "$", kind: "money", primary: false },
	{ key: "total_cost", label: "Total cost", unit: "$", kind: "money", primary: false },
];

/** Plain names for the line items the grid does not carry as columns. */
export const LINE_ITEM_LABEL: Record<string, string> = {
	...Object.fromEntries(HOUSE_COLUMNS.map((c) => [c.key, c.label])),
	avg_monthly_rent_collected: "Average rent collected per month",
	current_monthly_rent: "Current monthly rent",
	vacancy_loss: "Vacancy loss (billed − collected)",
	interest_rate: "Interest rate",
	land_cost: "Land cost",
	house_cost: "House cost",
	cost_per_sqft: "Cost per sq ft",
	appraised_value: "Appraised value",
	equity: "Equity (assessed value − loan)",
	purchase_price: "Purchase price (cost to build)",
	investment: "Investment (price − loan)",
	gross_rent_income: "Gross rent income",
	other_income: "Other income",
	vacancy_credit_loss: "Vacancy & credit loss",
	gross_operating_income: "Gross operating income",
	operating_expense: "Operating expense",
	noi: "Net operating income (NOI)",
	cost_seg_depreciation: "Cost segregation depreciation",
	taxable_income: "Taxable income",
	federal_tax_rate: "Federal tax rate",
	tax_operations: "Tax on operations",
	loan_payments: "Loan payments",
	cash_flow_before_taxes: "Cash flow before taxes",
	cash_flow_after_taxes: "Cash flow after taxes",
	grm: "Gross rent multiplier",
	cap_rate: "Cap rate",
	before_tax_cash_on_cash: "Cash on cash, before tax",
	after_tax_cash_on_cash: "Cash on cash, after tax",
	return_on_equity: "Return on equity",
	dcr: "Debt coverage ratio (DCR)",
	net_after_principal_and_capex: "Net after debt & capex",
	total_sqft: "Total sq ft",
	finished_sqft: "Finished sq ft",
	unfinished_sqft: "Unfinished sq ft",
	lot_sqft: "Lot sq ft",
	year_built: "Year built",
};

/** A definition written with code names ("rent_collected + ..."), in words. */
export const inWords = (text: string): string =>
	text.replace(/\b[a-z]+(?:_[a-z]+)+\b|\b(?:noi|dcr|hoa|grm)\b/g, (token) =>
		LINE_ITEM_LABEL[token] ? LINE_ITEM_LABEL[token].toLowerCase() : token,
	);

export const lineItemLabel = (key: string): string =>
	LINE_ITEM_LABEL[key] ?? key.replace(/_/g, " ");

/** Not added up on a total row: a ratio, or a figure split evenly. */
const NOT_SUMMED = new Set(["dcr", "vacancy_pct", "interest_rate", "cost_per_sqft"]);

export interface HouseCell {
	value: number | null;
	origin: Origin;
	fact: PortfolioFact | null;
}

export interface HouseRow {
	house: PortfolioHouse;
	cells: Record<string, HouseCell>;
	/** Collected at least SHORTFALL_FLAG under billed. */
	shortfall: boolean;
	/** The lease in force started in the year on screen. */
	newLease: boolean;
	leaseEnd: string | null;
}

const num = (value: unknown): number | null =>
	typeof value === "number" && Number.isFinite(value) ? value : null;

export const factKey = (houseId: string, lineItem: string): string =>
	`${houseId}::${lineItem}`;

export function indexFacts(
	facts: readonly PortfolioFact[],
	year: number,
): Map<string, PortfolioFact> {
	const index = new Map<string, PortfolioFact>();
	for (const fact of facts) {
		if (fact.year === year) index.set(factKey(fact.house_id, fact.line_item), fact);
	}
	return index;
}

export function houseRows(
	portfolio: Portfolio,
	year: number,
	platId: string | null,
): HouseRow[] {
	const facts = indexFacts(portfolio.tables.fact, year);
	return portfolio.tables.house
		.filter((house) => platId === null || house.plat_id === platId)
		.map((house) => {
			const cells: Record<string, HouseCell> = {};
			for (const column of HOUSE_COLUMNS) {
				if (column.key === "vacancy_pct") continue;
				const fact = facts.get(factKey(house.house_id, column.key)) ?? null;
				cells[column.key] = {
					value: num(fact?.value),
					origin: fact ? ORIGIN_OF_BASIS[fact.basis] : "workbook",
					fact,
				};
			}
			const billed = cells.rent_billed?.value ?? null;
			const loss = facts.get(factKey(house.house_id, "vacancy_loss"));
			const pct = billed && loss && num(loss.value) !== null ? (loss.value as number) / billed : null;
			cells.vacancy_pct = { value: pct, origin: "worked", fact: loss ?? null };
			const leaseStart = typeof house.lease_start === "string" ? house.lease_start : "";
			const leaseEnd = typeof house.lease_end === "string" && house.lease_end ? house.lease_end : null;
			return {
				house,
				cells,
				shortfall: pct !== null && pct >= SHORTFALL_FLAG,
				newLease: leaseStart.startsWith(String(year)),
				leaseEnd,
			};
		});
}

/** One total per summable column, over the rows given. */
export function totals(rows: readonly HouseRow[]): Record<string, number | null> {
	const out: Record<string, number | null> = {};
	for (const column of HOUSE_COLUMNS) {
		if (NOT_SUMMED.has(column.key) || column.kind !== "money") {
			out[column.key] = null;
			continue;
		}
		let sum = 0;
		let any = false;
		for (const row of rows) {
			const value = row.cells[column.key]?.value;
			if (value !== null && value !== undefined) {
				sum += value;
				any = true;
			}
		}
		out[column.key] = any ? sum : null;
	}
	const billed = out.rent_billed;
	const collected = out.rent_collected;
	out.vacancy_pct = billed && collected !== null ? (billed - collected) / billed : null;
	return out;
}

export interface PlatRow {
	platId: string;
	platName: string;
	houses: number;
	bank: string;
	rates: string;
	billed: number | null;
	collected: number | null;
	vacancyPct: number | null;
	netIncome: number | null;
	afterPrincipal: number | null;
	noi: PortfolioPlatFact | null;
	capRate: PortfolioPlatFact | null;
	dcr: PortfolioPlatFact | null;
	flags: string[];
}

export function platRows(portfolio: Portfolio, year: number): PlatRow[] {
	const platFact = (platId: string, item: string) =>
		portfolio.tables.plat_fact.find(
			(f) => f.plat_id === platId && f.year === year && f.line_item === item,
		) ?? null;
	return portfolio.manifest.plats.map((plat) => {
		const sums = totals(houseRows(portfolio, year, plat.plat_id));
		const dcr = platFact(plat.plat_id, "dcr");
		const flags: string[] = [];
		if (dcr && num(dcr.value) !== null && (dcr.value as number) < 1) flags.push("DCR under 1.0");
		// The DCR finding is the flag above; the rest are counted.
		const toCheck = portfolio.manifest.review.filter(
			(item) =>
				item.plat_id === plat.plat_id &&
				!item.id.startsWith("dcr-") &&
				!portfolio.review_state?.[item.id],
		).length;
		if (toCheck > 0) flags.push(`${toCheck} to check`);
		return {
			platId: plat.plat_id,
			platName: plat.plat_name,
			houses: plat.house_count,
			bank: plat.bank,
			rates: (plat.interest_rates_entered ?? "").split("/").filter(Boolean).map(formatRate).join(" or "),
			billed: sums.rent_billed,
			collected: sums.rent_collected,
			vacancyPct: sums.vacancy_pct,
			netIncome: sums.net_income,
			afterPrincipal: sums.net_after_principal_and_capex,
			noi: platFact(plat.plat_id, "noi"),
			capRate: platFact(plat.plat_id, "cap_rate"),
			dcr,
			flags,
		};
	});
}

/** "6.8750%" → "6.875%". */
function formatRate(text: string): string {
	const value = Number.parseFloat(text);
	return Number.isFinite(value) ? `${Number(value.toFixed(3))}%` : text;
}

export function formatMoney(value: number | null): string {
	if (value === null) return "—";
	const rounded = Math.round(value);
	const text = Math.abs(rounded).toLocaleString("en-US");
	return rounded < 0 ? `−${text}` : text;
}

export function formatValue(value: number | null, kind: HouseColumn["kind"]): string {
	if (value === null) return "—";
	if (kind === "pct") return `${value < 0 ? "−" : ""}${Math.abs(value * 100).toFixed(1)}%`;
	if (kind === "ratio") return value.toFixed(2);
	if (kind === "money") return formatMoney(value);
	return String(value);
}

/** A plat-level figure as its unit says. */
export function formatPlatFact(fact: PortfolioPlatFact | null): string {
	if (!fact || fact.value === null) return "—";
	if (fact.line_item === "cap_rate") return `${(fact.value * 100).toFixed(1)}%`;
	if (fact.unit === "ratio") return fact.value.toFixed(2);
	return formatMoney(fact.value);
}

/** "2026-08-31" → "Aug 2026". */
export function formatMonth(iso: string | null): string {
	if (!iso) return "—";
	const date = new Date(`${iso}T00:00:00Z`);
	if (Number.isNaN(date.getTime())) return iso;
	return date.toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
}

export function lineItemDefinition(
	portfolio: Portfolio,
	lineItem: string,
	scope: "house" | "plat" = "house",
): string {
	return (
		portfolio.tables.line_item.find((item) => item.scope === scope && item.line_item === lineItem)
			?.definition ?? ""
	);
}

/** The Files page for a workbook cell, when the workbook is in the deal room. */
export function workbookCellHref(
	portfolio: Portfolio,
	fact: { source_file: string; sheet: string; cell: string } | null,
): string | null {
	if (!fact) return null;
	const file = portfolio.source_files.find((entry) => entry.name === fact.source_file);
	if (!file?.attachment_id) return null;
	const params = new URLSearchParams({ sheet: fact.sheet, cell: fact.cell, cite: "excel" });
	return `/files/file/${file.attachment_id}?${params.toString()}`;
}

/** The median of the values, for "next to its neighbours" comparisons. */
export function median(values: readonly number[]): number | null {
	if (values.length === 0) return null;
	const sorted = [...values].sort((a, b) => a - b);
	const middle = Math.floor(sorted.length / 2);
	return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

/** A selection's line item for an added column's cell: `col:<id>`. */
export const ADDED_PREFIX = "col:";
export const addedKey = (columnId: string): string => `${ADDED_PREFIX}${columnId}`;
export const addedIdOf = (lineItem: string): string | null =>
	lineItem.startsWith(ADDED_PREFIX) ? lineItem.slice(ADDED_PREFIX.length) : null;

/** The columns that still read, in the order they were added. */
export const addedColumns = (portfolio: Portfolio): AddedColumn[] =>
	(portfolio.columns ?? []).filter((column) => !column.error);

export function addedValue(
	column: AddedColumn,
	houseId: string,
	year: number,
): AddedColumnValue | null {
	return column.values.find((v) => v.house_id === houseId && v.year === year) ?? null;
}

/** What a group of houses shows for the column: their average, or total. */
export function addedAggregate(
	column: AddedColumn,
	houseIds: readonly string[],
	year: number,
): number | null {
	if (column.aggregate === "none") return null;
	const wanted = new Set(houseIds);
	const values = column.values
		.filter((v) => v.year === year && wanted.has(v.house_id) && v.value !== null)
		.map((v) => v.value as number);
	if (values.length === 0) return null;
	const total = values.reduce((a, b) => a + b, 0);
	return column.aggregate === "mean" ? total / values.length : total;
}

export function formatAdded(value: number | null, column: AddedColumn): string {
	if (value === null) return "—";
	const digits = Math.max(0, Math.min(4, column.decimals));
	const scaled = column.kind === "pct" ? value * 100 : value;
	const text = Math.abs(scaled).toLocaleString("en-US", {
		minimumFractionDigits: digits,
		maximumFractionDigits: digits,
	});
	return `${scaled < 0 ? "−" : ""}${text}${column.kind === "pct" ? "%" : ""}`;
}

/** "mean" → "avg", as a plat or total row says it. */
export const AGGREGATE_LABEL: Record<AddedColumn["aggregate"], string> = {
	mean: "avg per house",
	sum: "total",
	none: "",
};
